import { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { App } from '@capacitor/app';
import { useFeatureToggle } from '@flopflip/react-redux';
import deviceTelemetry from 'app/services/deviceTelemetry';
import nativePush from 'app/services/nativePush';
import {
  accessTokenFactory,
  pushNotificationsSignalRConnection,
  pushNotificationsSignalRController,
} from 'app/services/signalR';
import _ from 'lodash';

import { SIGNAL_R_PUSH_NOTIFICATIONS_CONNECTION_URL } from 'constants/api';
import { COMPOSED_CLAIMS } from 'constants/composedClaims';
import { FeatureFlag } from 'constants/featureFlags';
import useCompanyId from 'hooks/useCompanyId';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import { logger } from 'utils/logger';
import { getIsNativePlatform } from 'utils/platform';

import { getUuid, selectIsClaimAllowed } from 'store/auth/selectors';

const PushNotificationSignalRContainer = ({ children }) => {
  const dispatch = useDispatch();

  const userId = useSelector(getUuid);
  const isNotificationCenterClaimAllowed = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.NOTIFICATION_CENTER_ACCESS),
  );

  const isNotificationCenterFeatureEnabled = useFeatureToggle(FeatureFlag.EnableNotificationCenter);

  const companyId = useCompanyId();

  const isNotificationCenterEnabled =
    isNotificationCenterClaimAllowed && isNotificationCenterFeatureEnabled;

  const isInitializedRef = useRef(false);
  const initPromiseRef = useRef();
  const isCleanedUpRef = useRef(false);

  const initConnection = useCallback(async () => {
    if (isInitializedRef.current || !userId || !companyId || !isNotificationCenterEnabled) {
      return;
    }

    isInitializedRef.current = true;

    pushNotificationsSignalRConnection.init(
      SIGNAL_R_PUSH_NOTIFICATIONS_CONNECTION_URL,
      accessTokenFactory,
    );

    await pushNotificationsSignalRConnection.start();

    if (isCleanedUpRef.current) {
      return;
    }

    pushNotificationsSignalRController.init(dispatch);
    pushNotificationsSignalRController.start(userId, companyId);
  }, [dispatch, userId, companyId, isNotificationCenterEnabled]);

  useEffectSafeStrictMode(() => {
    if (!userId || !isNotificationCenterEnabled) {
      return;
    }

    isCleanedUpRef.current = false;
    initPromiseRef.current = initConnection().catch(err => {
      logger.error('[PushNotifications SignalR] Failed to initialize connection', err);
      isInitializedRef.current = false;
    });

    return async () => {
      isCleanedUpRef.current = true;

      await initPromiseRef.current?.catch(_.noop);

      pushNotificationsSignalRController.stop();
      await pushNotificationsSignalRConnection.stop()?.catch(err => {
        logger.error('[PushNotifications SignalR] Failed to stop connection', err);
      });

      pushNotificationsSignalRConnection.resetConnection();
      isInitializedRef.current = false;
    };
  }, [initConnection, userId, isNotificationCenterEnabled]);

  // The OS push-permission prompt + device-token registration + heartbeat are decoupled from
  // Notification Center: they run for ANY authenticated native user so a tenant without the
  // notificationCenter claim/flag still gets a push token (closed-app delivery, VoIP ring-both).
  // The Notification Center claim/flag gates ONLY its SignalR feed (above) and the Header bell.
  // nativePush keeps its own #isRegistered idempotency, so this registers once.
  useEffectSafeStrictMode(() => {
    if (!getIsNativePlatform() || !userId || !companyId) {
      return;
    }

    nativePush
      .register({
        onPushToken: ({ pushToken, pushPlatform }) =>
          deviceTelemetry.register({ pushToken, pushPlatform }),
      })
      .catch(err => {
        logger.error('[PushNotifications Native] Failed to register device', err);
      });

    deviceTelemetry.register();
    deviceTelemetry.startHeartbeat();

    const resumeListenerPromise = App.addListener('resume', () => {
      nativePush.sendHeartbeat().catch(err => {
        logger.error('[PushNotifications Native] Failed to send heartbeat', err);
      });

      deviceTelemetry.sendHeartbeat();
    });

    return () => {
      resumeListenerPromise.then(listener => listener.remove()).catch(_.noop);

      deviceTelemetry.stopHeartbeat();

      nativePush.unregister().catch(err => {
        logger.error('[PushNotifications Native] Failed to unregister device', err);
      });
    };
  }, [userId, companyId]);

  return children;
};

export default PushNotificationSignalRContainer;
